
import {ProjectElement}                    from '@/models/project/project.element';

import {Project}                           from '@/models/project';
import {Logger}                            from "@/util/logger";
import {fromMySQLDate}                     from "@/util/util";

import {ProjectElementFactory}             from "@/models/project.element.factory";
import {ProjectElementType}                from "@/models/project/project.element.type";
import {TaskUpdate}                        from "@/models/task.update";
import {TaskProgressService}               from "@/services/task.progress.service";
import {RegressionElement}                 from '@/models/project/regression.element';
import {OperationalMeasureDialogParams}    from "@/gui/dialogs/operational.measure.dialog.params";
import {LocoQueryService}                  from "@/services/loco.query.service";
import {TaskBatchRegressionCreate}         from "@/models/task.batch.regression.create";
import {CorridorGroupElement}              from "@/models/project/corridor.group.element";
import {TaskBatchRegressionCreateFailed}   from "@/models/task.batch.regression.create.failed";
import {TaskBatchRegressionCreateComplete} from "@/models/task.batch.regression.create.complete";

import { v4 as uuidv4 }                    from 'uuid';

/**
 *
 * @class BatchRegressionCreator
 *
 * helper to take the parameters from a batch regression creation dialog and do
 * all the necessary work to actually create the regression and then add it as a leaf to
 * the Operational Measures branch in the user's current project.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      March 2022
 *
 */
export class BatchRegressionCreator {

  /** the user's current project */
  private _project: Project;

  /** standard constructor */
  constructor(
    project: Project
  ) {

    this._project                   = project;
    let brc: BatchRegressionCreator = this;

    Logger.debug('[BatchRegressionCreator] adding new Operational Measure Group...');

    /* go through a dialog to get the name and parameters for the model */

    brc._project.services.modals.open(
      'new-regression-modal',
      (value: OperationalMeasureDialogParams, resolve, reject) => {

        let err: string = brc.validate(value);

        if (err != '') {

          reject(err);
        }

        resolve(value);
      },
      (value: OperationalMeasureDialogParams) => {

        brc.create(value);

      });

  }

  /** the dialog parameters wer accepted, time to create a model! */
  create(value: OperationalMeasureDialogParams) {

    const brc: BatchRegressionCreator = this;

    Logger.debug('[BatchRegressionCreator] create: ', value);

    const loco: LocoQueryService      = brc._project.services.locoquery;

    /* who is the user? */
    let userid: string = brc._project.services.auth.userOfSession.username;

    /* if they gave the dates backwards, reverse them */

    let sd: Date = fromMySQLDate(value.start);
    let ed: Date = fromMySQLDate(value.end);

    if (ed.getTime() < sd.getTime()) {

      /* they are backwards */

      Logger.debug(`[BatchRegressionCreator] create - start/end are backwards, flipping...`);

      let tmp: string = value.start;
      value.start     = value.end;
      value.end       = tmp;

    }

    /*
     * we use the locoquery service to generate regression data for this time period
     * and this corridor (historical regression).
     *
     */

    const uuid: string           = uuidv4();
    const  projectFolder: string = this._project.dirname();
    let folder: string           = projectFolder + `/historical/regressions/${uuid}/`;

    /* add the new child model */

    Logger.debug(`[BatchRegressionCreator] create - new regression model (${userid}): `, value);

    const tasks: TaskProgressService = brc._project.services.taskMonitor;
    const taskId: string             = TaskUpdate.generateId();

    tasks.progress(new TaskBatchRegressionCreate(taskId, `Creating (might take a few minutes): ${value.corridor} ...`, 1.0));

    /* start the creation of the model... */

    loco.historicalRegression(value.corridor, value.start, value.end, folder).then(
      (result: {key: string}) => {

        Logger.debug('[BatchRegressionCreator] received fake blobs: ', result);

        let group: ProjectElement = brc._project.operationalMeasures();

        /* create a corridor group to file this one under... */

        /* look for an existing child corridor of the same name, if we don't find it...then create it. */

        let cg: CorridorGroupElement = null;

        const peers: ProjectElement[] = group._children;

        for (const peer of peers) {

          if (peer._label === value.corridor) {

            /* we found it, was already created previously */

            cg = peer as CorridorGroupElement;

            Logger.debug(`[BatchRegressionCreator] using existing corridor (${value.corridor}).`);

          }

        }

        if (!cg) {

          /* this corridor doesn't exist yet ... */

          Logger.debug(`[BatchRegressionCreator] first time create on corridor (${value.corridor}).`);

          cg = ProjectElementFactory.fromObject(
            ProjectElementType.CorridorGroup,
            {
              project: this._project,
                   id: '',
                 name: value.corridor,
               parent: group,
              publish: true
            }) as CorridorGroupElement;
        }

        /* add the fake result ... */

        Logger.debug('[BatchRegressionCreator] adding regression...');

        const el: RegressionElement = ProjectElementFactory.fromObject(
          ProjectElementType.RegressionResult,
          {
            project: this._project,
            id:      '',
            input:   result.key,
            start:   value.start,
            end:     value.end,
            parent:  cg,
            publish: true
          }) as RegressionElement;

        brc._project.expand([group]);

        brc._project.expand([cg]);

        el.save();

        tasks.progress(new TaskBatchRegressionCreateComplete(taskId, `[${value.corridor}] Regression ready!`));

        Logger.debug('[BatchRegressionCreator] COMPLETE!');

      },
      (error) => {

        /* track the error */

        tasks.progress(new TaskBatchRegressionCreateFailed(taskId, error));

        /* log it */

        Logger.error('[BatchRegressionCreator] create - could not create regression model: ', error);

      });

  }

  /** validate the parameters we got from the dialog */
  validate(value: OperationalMeasureDialogParams): string {

      let brc: BatchRegressionCreator      = this;
      let parent: ProjectElement           = brc._project.operationalMeasures();
      let corridor: string                 = value.corridor;

      Logger.debug('[BatchRegressionCreator] (new regression) validating ...');

      /* is it a valid name? */

      if (corridor.trim() === '') {

        return 'Corridor can not be empty.';
      }

      /* do we already have a model of this name? */

      const peers: ProjectElement[] = parent._children;

      for (const peer of peers) {

        /*
        if (peer._label === corridor) {

          return 'Corridor already exists in this operational model.';
        }

         */

      }

      if (value.start === '') {

        return 'Must set start date';
      }

      if (value.end === '') {

        return 'Must set end date';
      }

      /* its golden! */

      value.corridor = corridor;

      return '';
    }

}
